// ── 상황판 유틸 함수 및 상수 ──────────────────────────────────────────────────
import { useEffect, useState } from 'react';
import { toDateString, todayKST, nowKST, addDaysUtc, dayOfWeekUTC, toKoreanDateTimeString, toKSTTimeString } from '../../../utils/date';
import { applyRegionReform } from '../../../utils/regionReform';

// ─── 모바일 감지 훅 ───────────────────────────────────────────────────────────
export function useIsMobile() {
  const [v, setV] = useState(() => typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  useEffect(() => {
    const h = () => setV(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return v;
}

// ─── 날짜/시간 헬퍼 ──────────────────────────────────────────────────────────
/** KST 기준 오늘 YYYY-MM-DD */
export { todayKST as getKstYmd } from '../../../utils/date';
/** KST 기준 다음 영업일 YYYY-MM-DD */
export function getNextBusinessDayKstYmd(holidays) {
  // todayKST, addDaysUtc, dayOfWeekUTC: utils/date.js 사용 (new Date 직접 사용 금지)
  let ymd = addDaysUtc(todayKST(), 1);
  for (let i = 0; i < 14; i++) {
    const dow = dayOfWeekUTC(ymd);
    if (dow !== 0 && dow !== 6 && !holidays.has(ymd)) return ymd;
    ymd = addDaysUtc(ymd, 1);
  }
  return ymd;
}
/** 주어진 날짜가 내일인지 */
export function isTomorrow(ymd) {
  // addDaysUtc, todayKST: utils/date.js 사용 (new Date 직접 사용 금지)
  return addDaysUtc(todayKST(), 1) === ymd;
}
/** KST 기준 현재 연/월 */
export function getCurrentYearMonthKst() {
  // nowKST: utils/date.js 사용 (new Date 직접 사용 금지)
  const now = nowKST();
  return { year: now.getUTCFullYear(), month: now.getUTCMonth() + 1 };
}

// ─── 데이터 포맷 헬퍼 ────────────────────────────────────────────────────────
/** IPv6-mapped IPv4 제거 */
export function normalizeIp(ip) {
  return String(ip || '').replace(/^::ffff:/, '').trim();
}
/** 날짜+시간 포맷 (로케일) */
export function fmtDateTime(value) {
  // toKoreanDateTimeString: utils/date.js 사용 (new Date 직접 사용 금지)
  return toKoreanDateTimeString(value);
}
/** 시각만 포맷 HH:MM */
export function fmtTime(value) {
  if (!value) return '-';
  // toKSTTimeString: utils/date.js 사용 (new Date 직접 사용 금지)
  const result = toKSTTimeString(value);
  return result.includes('NaN') ? '-' : result;
}
/** API 응답에서 배열 추출 */
export function toList(response) {
  if (!response?.ok) return [];
  if (Array.isArray(response.data?.list)) return response.data.list;
  if (Array.isArray(response.data?.data)) return response.data.data;
  if (Array.isArray(response.data)) return response.data;
  return [];
}
/** rows에서 중복 없는 이름 정렬 배열 */
export function uniqueNames(rows) {
  const set = new Set();
  (rows || []).forEach((row) => {
    const name = String(row?.name || '').trim();
    if (name) set.add(name);
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'ko-KR'));
}
/** YYYY-MM-DD → M/D */
export function toMd(dateText) {
  const text = toDateString(dateText || '');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return '-';
  return `${Number(text.slice(5, 7))}/${Number(text.slice(8, 10))}`;
}
/** 연차 일정 행 여부 (생성/자동생성/포상/정산/정정 제외) */
export function isScheduleLeaveRow(row) {
  return !['생성', '자동생성', '포상', '정산', '정정'].includes(String(row?.type || '').trim());
}

// ─── 지역 상수 ───────────────────────────────────────────────────────────────
export const REGION_METRO_FULL = {
  '서울특별시': '서울', '부산광역시': '부산', '대구광역시': '대구',
  '인천광역시': '인천', '광주광역시': '광주', '대전광역시': '대전',
  '울산광역시': '울산', '세종특별자치시': '세종', '세종시': '세종',
};
export const REGION_METRO_SHORT = new Set(Object.values(REGION_METRO_FULL));
export const REGION_DO_FULL = {
  '경기도': '경기', '강원도': '강원', '충청북도': '충북', '충청남도': '충남',
  '전라북도': '전북', '전라남도': '전남', '경상북도': '경북', '경상남도': '경남',
  '제주특별자치도': '제주',
  // 특별자치도 개편 명칭 (2023-06 강원 / 2024-01 전북) — 누락돼 있던 기존 버그 보강 (2026-09-01)
  '강원특별자치도': '강원', '전북특별자치도': '전북',
};
export const REGION_DO_SHORT_SET = new Set(Object.values(REGION_DO_FULL));

/** 주소 문자열에서 시/도 + 시/군/구 추출 */
export function extractRegion(address) {
  if (!address) return '-';
  // "인천서구"처럼 광역시+구가 붙은 토큰 분리 (2026-09-01 — 백엔드 파서와 동일 규칙)
  const tokens = String(address).trim().split(/\s+/).flatMap((t) => {
    const m = /^(서울|부산|대구|인천|광주|대전|울산|세종)(\S+[구군])$/.exec(t);
    return m ? [m[1], m[2]] : [t];
  });
  const parts = [];
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    if (!token) continue;
    if (/\d/.test(token)) break;
    if (/[읍면동리로길]$/.test(token)) break;
    if (REGION_METRO_FULL[token]) { parts.push(REGION_METRO_FULL[token]); continue; }
    if (REGION_METRO_SHORT.has(token)) { parts.push(token); continue; }
    if (REGION_DO_FULL[token]) { parts.push(REGION_DO_FULL[token]); continue; }
    if (token.endsWith('시')) {
      const withoutSi = token.slice(0, -1);
      const prevIsdo = parts.length > 0 && REGION_DO_SHORT_SET.has(parts[parts.length - 1]);
      parts.push((prevIsdo || !REGION_METRO_SHORT.has(withoutSi)) ? withoutSi : token);
      if (!/[구군]$/.test(tokens[i + 1] || '')) break;
      continue;
    }
    if (/군$/.test(token)) { parts.push(token); break; }
    if (/구$/.test(token)) { parts.push(token); break; }
    parts.push(token);
  }
  // 행정구역 개편 매핑 (2026-09-01) — 옛 명칭(인천 서구 등)을 현행 명칭으로. 백엔드 region 과 그룹핑 일치.
  const raw = parts.join(' ');
  if (!raw) return '-';
  return applyRegionReform(raw, tokens) || '-';
}
/** 도 접두어 제거하여 시/군/구만 표시 */
export function formatRegion(region) {
  if (!region || region === '-') return region;
  const parts = String(region).split(' ');
  if (parts.length >= 2 && REGION_DO_SHORT_SET.has(parts[0])) {
    return parts.slice(1).join(' ');
  }
  return region;
}

// ─── 아바타 색상 ─────────────────────────────────────────────────────────────
/** 이름 해시 기반 아바타 색상 */
export function nameColor(name) {
  const colors = ['#3b82f6','#8b5cf6','#06b6d4','#10b981','#f59e0b','#ef4444','#ec4899','#6366f1'];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return colors[h % colors.length];
}

// ─── 한국 지도: 시/도 → SVG 좌표 ─────────────────────────────────────────────
export const REGION_COORDS = {
  '서울': { x: 96, y: 50 }, '경기': { x: 96, y: 62 }, '인천': { x: 78, y: 55 },
  '강원': { x: 148, y: 50 }, '충북': { x: 126, y: 82 }, '충남': { x: 80, y: 90 },
  '세종': { x: 106, y: 88 }, '대전': { x: 112, y: 95 }, '경북': { x: 158, y: 90 },
  '대구': { x: 152, y: 115 }, '경남': { x: 145, y: 140 }, '부산': { x: 170, y: 162 },
  '울산': { x: 178, y: 140 }, '전북': { x: 105, y: 118 }, '전남': { x: 92, y: 160 },
  '광주': { x: 88, y: 145 }, '제주': { x: 85, y: 238 },
};

/** 지역 문자열 첫 단어에서 시/도 코드 추출 */
export function extractSido(region) {
  const first = String(region || '').trim().split(/\s+/)[0] || '';
  if (first.startsWith('서울')) return '서울';
  if (first.startsWith('경기')) return '경기';
  if (first.startsWith('인천')) return '인천';
  if (first.startsWith('강원')) return '강원';
  if (first.startsWith('충청북') || first.startsWith('충북')) return '충북';
  if (first.startsWith('충청남') || first.startsWith('충남')) return '충남';
  if (first.startsWith('세종')) return '세종';
  if (first.startsWith('대전')) return '대전';
  if (first.startsWith('경상북') || first.startsWith('경북')) return '경북';
  if (first.startsWith('대구')) return '대구';
  if (first.startsWith('경상남') || first.startsWith('경남')) return '경남';
  if (first.startsWith('부산')) return '부산';
  if (first.startsWith('울산')) return '울산';
  if (first.startsWith('전라북') || first.startsWith('전북')) return '전북';
  if (first.startsWith('전라남') || first.startsWith('전남')) return '전남';
  if (first.startsWith('광주')) return '광주';
  if (first.startsWith('제주')) return '제주';
  return null;
}
